import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api, tokens } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { AUTH_CARD, AUTH_INPUT, AUTH_LINK, AUTH_TITLE, AuthAlert, AuthLabel, AuthShell } from '../components/AuthShell';
import { Button, Field, Input } from '../components/ui';

/** The system the emailed link comes back to: this one. */
const SYSTEM = 'ADMIN';

/**
 * Forgot password (operator, 2026-10-29: build the flow). Both pages are open to
 * anyone, signed in or not — the email's link lands on the second one.
 *
 * Route: `/forgot-password`. Asks for the email someone signs in with; the API
 * emails a link to set a new password, good once within the hour. The answer is
 * the same whether or not the address is someone's.
 */
export function ForgotPasswordPage() {
  const { t } = useI18n();
  const location = useLocation();
  const [email, setEmail] = useState(() => String((location.state as { email?: string } | null)?.email ?? ''));
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim(), app: SYSTEM });
      setSentTo(email.trim());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      {sentTo ? (
        <div className={AUTH_CARD} role="status">
          <h2 className={AUTH_TITLE}>{t('auth.forgotTitle')}</h2>
          <p className="text-body-sm text-neutral-200">{t('auth.resetLinkSent', { email: sentTo })}</p>
          <p className="mt-3 text-body-sm text-neutral-400">{t('auth.resetLinkSentHint')}</p>
          <div className="mt-5">
            <Link to="/" className={AUTH_LINK}>
              {t('auth.backToSignIn')}
            </Link>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className={AUTH_CARD}>
          <h2 className={AUTH_TITLE}>{t('auth.forgotTitle')}</h2>
          <p className="mb-4 text-body-sm text-neutral-300">{t('auth.forgotIntro')}</p>

          <Field label={<AuthLabel>{t('auth.emailLabel')}</AuthLabel>} required>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
              autoFocus
              dir="ltr"
              placeholder="you@viganium.net"
              className={`${AUTH_INPUT} placeholder:!text-[var(--text-tertiary)] dark:placeholder:!text-[var(--text-secondary)]`}
            />
          </Field>

          {error && <AuthAlert>{error}</AuthAlert>}

          <Button type="submit" variant="primary" size="lg" loading={busy} disabled={!email.trim()} className="mt-5 w-full">
            {t('auth.sendResetLink')}
          </Button>
          <div className="mt-4 text-center">
            <Link to="/" className={AUTH_LINK}>
              {t('auth.backToSignIn')}
            </Link>
          </div>
        </form>
      )}
    </AuthShell>
  );
}

type FieldName = 'next' | 'again';

/**
 * Route: `/reset-password?token=…` — where the emailed link lands. Sets the new
 * password, held to Settings › Security's minimum (the API says what it is when a
 * shorter one is sent: this page cannot read the settings signed out). It is one
 * password everywhere, so every device signed in with the old one is signed out,
 * this browser too.
 */
export function ResetPasswordPage() {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  // Read once, then taken out of the address bar, so it stays out of the history.
  const [token] = useState(() => new URLSearchParams(location.search).get('token') ?? '');
  const [form, setForm] = useState<Record<FieldName, string>>({ next: '', again: '' });
  const [show, setShow] = useState(false);
  const [minLength, setMinLength] = useState(8);
  // Kept as a key, not text, so it follows the language when it is switched.
  const [problem, setProblem] = useState<{ field: FieldName; key: TranslationKey; vars?: Record<string, number> } | null>(null);
  const [outcome, setOutcome] = useState<'done' | 'invalid' | null>(token ? null : 'invalid');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (location.search) navigate(location.pathname, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (form.next.length < minLength) {
      setProblem({ field: 'next', key: 'auth.passwordTooShort', vars: { n: minLength } });
      return;
    }
    if (form.next !== form.again) {
      setProblem({ field: 'again', key: 'auth.passwordMismatch' });
      return;
    }
    setProblem(null);
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { token, newPassword: form.next });
      // Every session of the old password has ended, this browser's with it.
      tokens.clear();
      setOutcome('done');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TOKEN_INVALID') {
        setOutcome('invalid');
      } else if (err instanceof ApiError && err.code === 'VALIDATION_FAILED' && (err.details as any)?.minLength) {
        const n = Number((err.details as any).minLength);
        setMinLength(n);
        setProblem({ field: 'next', key: 'auth.passwordTooShort', vars: { n } });
      } else {
        setError(err instanceof ApiError ? err.message : t('common.error'));
      }
    } finally {
      setBusy(false);
    }
  };

  const set = (field: FieldName) => (e: { target: { value: string } }) => {
    setForm({ ...form, [field]: e.target.value });
    if (problem?.field === field) setProblem(null);
  };
  const errorFor = (field: FieldName) => (problem?.field === field ? t(problem.key, problem.vars) : null);

  if (outcome === 'done') {
    return (
      <AuthShell>
        <div className={AUTH_CARD} role="status">
          <h2 className={AUTH_TITLE}>{t('auth.resetTitle')}</h2>
          <p className="text-body-sm text-neutral-200">{t('auth.passwordResetDone')}</p>
          {/* A fresh load: whatever session this browser held is gone. */}
          <Button variant="primary" size="lg" className="mt-5 w-full" onClick={() => window.location.replace('/')}>
            {t('auth.signIn')}
          </Button>
        </div>
      </AuthShell>
    );
  }

  if (outcome === 'invalid') {
    return (
      <AuthShell>
        <div className={AUTH_CARD} role="alert">
          <h2 className={AUTH_TITLE}>{t('auth.resetTitle')}</h2>
          <p className="text-body-sm text-neutral-200">{t('auth.resetLinkInvalid')}</p>
          <div className="mt-5 flex items-center justify-between gap-3">
            <Link to="/forgot-password" className={AUTH_LINK}>
              {t('auth.requestNewLink')}
            </Link>
            <Link to="/" className={AUTH_LINK}>
              {t('auth.backToSignIn')}
            </Link>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <form onSubmit={onSubmit} className={AUTH_CARD}>
        <h2 className={AUTH_TITLE}>{t('auth.resetTitle')}</h2>
        <p className="mb-4 text-body-sm text-neutral-300">{t('auth.resetIntro')}</p>

        <div className="space-y-4">
          <Field
            label={<AuthLabel>{t('auth.newPassword')}</AuthLabel>}
            required
            hint={t('auth.passwordHint', { n: minLength })}
            error={errorFor('next')}
          >
            <div className="flex gap-2">
              <Input
                type={show ? 'text' : 'password'}
                dir="ltr"
                autoComplete="new-password"
                value={form.next}
                onChange={set('next')}
                autoFocus
                className={`${AUTH_INPUT} min-w-0 flex-1`}
              />
              <Button
                icon={show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                aria-label={show ? t('auth.hidePassword') : t('auth.showPassword')}
                title={show ? t('auth.hidePassword') : t('auth.showPassword')}
                onClick={() => setShow(!show)}
              />
            </div>
          </Field>
          <Field label={<AuthLabel>{t('auth.confirmPassword')}</AuthLabel>} required error={errorFor('again')}>
            <Input
              type={show ? 'text' : 'password'}
              dir="ltr"
              autoComplete="new-password"
              value={form.again}
              onChange={set('again')}
              className={AUTH_INPUT}
            />
          </Field>
        </div>

        {error && <AuthAlert>{error}</AuthAlert>}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={busy}
          disabled={!form.next || !form.again}
          className="mt-5 w-full"
        >
          {t('auth.setNewPassword')}
        </Button>
      </form>
    </AuthShell>
  );
}
